import { getWorkspace } from "@/api/workspace";
import type { Workspace } from "@/api/workspace";
import type { NavItem } from "@/mk/types";

/** Width of the sidebar in its icon-only state: the narrowest it may become. */
export const PM_SIDEBAR_MIN = 78;
/** Width of the sidebar when it first appears: the comfortable reading size. */
export const PM_SIDEBAR_DEFAULT = 268;
/** The sidebar never grows past this: wider would steal the page itself. */
export const PM_SIDEBAR_MAX = 420;

const WIDTH_KEY = "pm_sidebar_width";
const ORDER_KEY = "pm_sidebar_order";

export function readSidebarWidth(): number {
    try {
        const raw = window.localStorage.getItem(WIDTH_KEY);
        const value = raw ? Number.parseInt(raw, 10) : NaN;
        if (Number.isFinite(value)) {
            return Math.min(PM_SIDEBAR_MAX, Math.max(PM_SIDEBAR_MIN, value));
        }
    } catch {
        return PM_SIDEBAR_DEFAULT;
    }
    return PM_SIDEBAR_DEFAULT;
}

export function writeSidebarWidth(value: number) {
    const clamped = Math.min(PM_SIDEBAR_MAX, Math.max(PM_SIDEBAR_MIN, Math.round(value)));
    try {
        window.localStorage.setItem(WIDTH_KEY, String(clamped));
    } catch {
        return;
    }
}

/** Each workspace keeps its own order: arranging the design menu must never
 *  disturb the execution one, even for the pages they share. */
function orderKey(mode: Workspace): string {
    return `${ORDER_KEY}:${mode}`;
}

export function readSidebarOrder(mode: Workspace = getWorkspace()): string[] {
    try {
        let raw = window.localStorage.getItem(orderKey(mode));
        // An order saved before the workspaces split moves, once, into the
        // first workspace that asks for it, so no reader loses his menu.
        if (raw === null) {
            raw = window.localStorage.getItem(ORDER_KEY);
            if (raw !== null) {
                window.localStorage.setItem(orderKey(mode), raw);
                window.localStorage.removeItem(ORDER_KEY);
            }
        }
        if (!raw) {
            return [];
        }
        const parsed: unknown = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === "string") : [];
    } catch {
        return [];
    }
}

export function writeSidebarOrder(ids: string[], mode: Workspace = getWorkspace()) {
    try {
        window.localStorage.setItem(orderKey(mode), JSON.stringify(ids));
    } catch {
        return;
    }
}

/** Top-level applications in the reader's own order; anything new or unknown
 *  keeps its default place at the end, so the menu never loses an entry. */
export function applySidebarOrder(items: NavItem[], order: string[]): NavItem[] {
    if (!order.length) {
        return items;
    }
    const rank = new Map(order.map((id, index) => [id, index]));
    return [...items].sort((a, b) => {
        const ra = rank.get(a.id);
        const rb = rank.get(b.id);
        if (ra === undefined && rb === undefined) {
            return 0;
        }
        if (ra === undefined) {
            return 1;
        }
        if (rb === undefined) {
            return -1;
        }
        return ra - rb;
    });
}
